// Support Chat API
import { axiosInstance } from './index'
import imageCompression from 'browser-image-compression'

const basePath = '/support'

/**
 * Получить сообщения пользователя с пагинацией
 * @param {number} perPage - Количество сообщений на страницу (максимум 50)
 * @param {number} lastMessageId - ID последнего загруженного сообщения для ленивой загрузки
 * @returns {Promise} - Промис с данными ответа
 */
export const getMessagesAPI = async ({ perPage = 20, lastMessageId = null } = {}) => {
  const params = { per_page: perPage }
  if (lastMessageId) {
    params.last_message_id = lastMessageId
  }

  const response = await axiosInstance.get(`${basePath}/messages`, { params })
  return response.data
}

/**
 * Отправить текстовое сообщение или сообщение с файлом
 * @param {string} message - Текст сообщения
 * @param {File} file - Файл для загрузки (опционально)
 * @returns {Promise} - Промис с данными ответа
 */
export const sendMessageAPI = async ({ message, file = null }) => {
  let requestData
  let headers = {}

  if (file) {
    // Обработка файла с сжатием изображений
    let processedFile = file
    const isImage = file.type.startsWith('image/')

    if (isImage) {
      const options = {
        maxSizeMB: 1,               // не больше 1 МБ
        maxWidthOrHeight: 1024,     // не больше 1024px по ширине/высоте
        useWebWorker: true,         // в Web Worker, чтобы не блокировать UI
        fileType: file.type         // сохраняем оригинальный тип
      }

      try {
        processedFile = await imageCompression(file, options)
        //console.log('Исходный файл:', (file.size / 1024).toFixed(2), 'KB')
        //console.log('Сжатый файл:', (processedFile.size / 1024).toFixed(2), 'KB')
      } catch (error) {
        console.error('Ошибка сжатия изображения:', error)
        // Если сжатие не удалось, используем оригинальный файл
        processedFile = file
      }
    }

    // Проверка размера файла после сжатия (макс 10MB)
    if (processedFile.size > 10 * 1024 * 1024) {
      throw new Error('Файл слишком большой. Максимальный размер: 10MB')
    }

    // Создаем FormData для загрузки файла
    const formData = new FormData()
    formData.append('message', message)
    formData.append('file', processedFile, file.name)

    // Добавляем информацию о размерах для аналитики
    formData.append('original_size', file.size)
    formData.append('compressed_size', processedFile.size)

    requestData = formData
    headers['Content-Type'] = 'multipart/form-data'
  } else {
    // Обычное текстовое сообщение
    requestData = { message }
    headers['Content-Type'] = 'application/json'
  }

  const response = await axiosInstance.post(`${basePath}/message`, requestData, { headers })
  return response.data
}

/**
 * Получить количество непрочитанных сообщений
 * @returns {Promise} - Промис с данными ответа
 */
export const getUnreadCountAPI = async () => {
  const response = await axiosInstance.get(`${basePath}/unread-count`)
  return response.data
}

/**
 * Отметить все сообщения как прочитанные
 * @returns {Promise} - Промис с данными ответа
 */
export const markAsReadAPI = async () => {
  const response = await axiosInstance.post(`${basePath}/mark-read`)
  return response.data
}

/**
 * Утилита для сжатия изображений перед отправкой
 * @param {File} file - Файл изображения
 * @param {Object} options - Опции сжатия
 * @returns {Promise<File>} - Сжатый файл
 */
export const compressImageFile = async (file, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
    fileType: file.type,
    ...options
  }

  if (!file.type.startsWith('image/')) {
    return file // Возвращаем исходный файл, если это не изображение
  }

  try {
    const compressedFile = await imageCompression(file, defaultOptions)
    //console.log(`Сжатие изображения: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB`)
    return compressedFile
  } catch (error) {
    console.error('Ошибка сжатия изображения:', error)
    return file // В случае ошибки возвращаем исходный файл
  }
}

/**
 * Проверка допустимости файла
 * @param {File} file - Файл для проверки
 * @param {number} maxSizeMB - Максимальный размер в МБ
 * @param {string[]} allowedTypes - Разрешенные MIME типы
 * @returns {Object} - Результат валидации
 */
export const validateFile = (file, maxSizeMB = 10, allowedTypes = [
  'image/jpeg', 'image/jpg', 'image/png',
  'application/pdf', 'text/plain',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip', 'application/x-rar-compressed'
]) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Файл слишком большой. Максимальный размер: ${maxSizeMB}MB`,
      code: 'FILE_TOO_LARGE'
    }
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Неподдерживаемый тип файла',
      code: 'INVALID_FILE_TYPE'
    }
  }

  return {
    valid: true,
    error: null,
    code: null
  }
}